/**
 * pricing.ts — LawKit 요금 및 계약 안내 v2 data.
 *
 * Four section kinds (one per anchor on the /plan page):
 *   - 'service-groups' → 단건: sub-grouped tables (계약서 / 법률 문서 / 법인 행정서류 / 기타 서류).
 *   - 'bundle-groups'  → 번들: multiple sub-bundle groups, each with 3 tier cards
 *                        comparing 단건 합산 (strike-through) vs. 번들가.
 *   - 'group-table'    → 단체: single info row with benefits + 권장 대상 bullets.
 *   - 'memberships'    → 멤버십: 단일 Retainer 테이블 (브론즈 / 실버 / 골드) — 금액 크레딧 기반.
 *
 * All amounts are VAT-excluded as shown in the source guide.
 */

import { MEMBERSHIP_PLANS } from '@/lib/constants/membership-plans';
import { bonusRateLabel } from '@/utils/membership-plans';
import type { MembershipTier } from '@/lib/types/membership';
import { formatWon } from '@/lib/catalog/format';

// ── Shared ──────────────────────────────────────────────────────────────
export interface PricingFeature {
  ttl: string;
  dsc: string;
}

// ── 단건 (service-groups) ───────────────────────────────────────────────
//
// **단건 표에는 금액이 없다.** 상품·금액은 서버 카탈로그에서 온다
// (`catalogApi.getProducts()` → `CatalogGroups`). 여기 남는 것은 섹션 머리말뿐이다.
//
// 예전에는 24행짜리 `ServiceRow[]` 를 손으로 들고 있었다. 같은 항목의 금액이 이 파일과
// FAQ·영문 요금표에 따로 적혀 있어서 하나를 고치면 나머지가 어긋났고, 두 값이 다르면
// 그게 곧 클레임이다. 금액을 다시 이 파일로 들여오지 말 것.

// ── 번들 (bundle-groups) ────────────────────────────────────────────────
export interface BundleTier {
  name: string;
  /** Included services, split into bullets. */
  inclusions: string[];
  /** 단건 합산 — original total, rendered with strikethrough. */
  original: string;
  /** 번들가 — discounted bundle price, rendered prominently. */
  price: string;
  /** Highlight this tier visually (gradient bg + accent border). */
  featured?: boolean;
  /** Optional pill above the card, e.g. "추천". */
  tag?: string;
}
export interface BundleGroup {
  /** Optional leading emoji, e.g. "🌱". */
  emoji?: string;
  name: string;
  /** Italic quote shown under the bundle name. */
  tagline?: string;
  tiers: BundleTier[];
  /** Optional "권장 대상" bullets shown beneath the tier grid. */
  targetList?: string[];
}

// ── 멤버십 (memberships) ────────────────────────────────────────────────
/**
 * 요금 페이지가 그리는 멤버십 등급 한 줄.
 *
 * **금액은 이 파일이 정하지 않는다.** `lib/constants/membership-plans` 가 유일한
 * 출처이고 여기서는 그것을 문자열로 옮기기만 한다(`MEMBERSHIP_ROWS`). 예전에는 같은
 * 숫자가 두 파일에 따로 적혀 있었고, 실제로 갈렸다 — 마이페이지는 크레딧 60만원을
 * 보여주는데 요금표는 50만원이었다. 등급 금액을 바꿀 일이 생기면 상수 파일만 고친다.
 */
export interface MembershipRow {
  tier: MembershipTier;
  /** 화면 표기명. 시안이 영문(Bronze/Silver/Gold)을 쓴다 — 마이페이지의 한글 라벨과 별개다 */
  name: string;
  /** 카드 제목 아래 한 줄 설명 */
  subtitle: string;
  /** 월 요금(VAT 별도) */
  monthlyPrice: string;
  /** 월 금액 크레딧 */
  creditAmount: string;
  /** 납부액 대비 더 받는 몫, e.g. "+20%". 두 금액에서 계산한다 */
  bonus: string;
}

// ── Section discriminated union ─────────────────────────────────────────
interface PricingSectionBase {
  /** `PLAN_ANCHOR` 와 같은 값. `/plan` 안의 구획 id 다 */
  id: string;
  /** 헤더 메뉴·푸터·모바일 서랍이 쓰는 짧은 이름 */
  label: string;
  /** 구획이 내세우는 세 가지. 단체 카드의 체크 목록이 이걸 그린다 */
  features: PricingFeature[];
  /** Per-section notes (※) rendered above the generic guideline notice. */
  notes?: string[];
}

export type PricingSection =
  | (PricingSectionBase & { kind: 'service-groups' })
  | (PricingSectionBase & { kind: 'bundle-groups'; bundleGroups: BundleGroup[] })
  | (PricingSectionBase & { kind: 'group-table' })
  | (PricingSectionBase & { kind: 'memberships'; rows: MembershipRow[] });

/**
 * 등급별 화면 문구. **금액은 없다** — 이름과 한 줄 설명뿐이다.
 *
 * 마이페이지(`messages/ko/membership.json` 의 `page.reco.subtitle`)와 문장이 다르다.
 * 그쪽은 이미 가입한 사람에게 "당신 사용량엔 이게 맞다"를 말하고, 여기는 처음 보는
 * 사람에게 "어떤 회사가 쓰는 플랜인가"를 말한다. 같은 등급이지만 같은 문장이 아니다.
 */
const TIER_COPY: Record<MembershipTier, { name: string; subtitle: string }> = {
  BRONZE: { name: 'Bronze', subtitle: '가볍게 시작하는 상시 자문' },
  SILVER: { name: 'Silver', subtitle: '계약이 잦은 팀의 표준 플랜' },
  GOLD: { name: 'Gold', subtitle: '사내 법무팀처럼 쓰는 최상위 플랜' },
};

export const MEMBERSHIP_ROWS: MembershipRow[] = MEMBERSHIP_PLANS.map(plan => ({
  tier: plan.tier,
  name: TIER_COPY[plan.tier].name,
  subtitle: TIER_COPY[plan.tier].subtitle,
  monthlyPrice: formatWon(plan.monthlyPrice),
  creditAmount: formatWon(plan.monthlyCredit),
  // 마이페이지 추천 카드와 같은 함수다 — 두 화면의 "+20%" 가 갈릴 자리를 만들지 않는다.
  bonus: bonusRateLabel(plan),
}));

/** "Bronze는 월 500,000원으로 600,000원어치" — 등급이 늘면 문장도 같이 는다. */
const creditNote = `${MEMBERSHIP_ROWS.map(row => `${row.name}는 월 ${row.monthlyPrice}으로 ${row.creditAmount}어치`).join(', ')} — 상위 등급일수록 혜택이 커집니다`;

// ── 한 장짜리 /plan 의 앵커와 상단 카드 ──────────────────────────────────

/**
 * `/plan` 안의 구획 id. **경로가 아니라 앵커다.**
 *
 * 예전에는 단건·단체·멤버십이 각자 라우트였고 탭이 그 사이를 오갔다. 세 화면이
 * 머리말만 다르고 나머지가 같아서, 고르기 전에 비교하려면 페이지를 세 번 왕복해야 했다.
 * 지금은 한 장이고 여기 id 로 내려간다. 옛 경로는 `next.config.mjs` 가 이 앵커로 보낸다 —
 * 밖에서 걸린 링크와 검색 결과가 살아 있어서 지우면 안 된다.
 */
export const PLAN_ANCHOR = {
  membership: 'membership',
  group: 'group',
  priceGuide: 'price-guide',
} as const;

/** 상단 카드 한 장. 가격은 옵션이다 — 단건은 카탈로그 최저가라 화면이 채운다. */
export interface PlanCard {
  key: 'single' | 'membership';
  name: string;
  /** 누구를 위한 플랜인가 */
  who: string;
  /** 금액 뒤에 붙는 단위 문구 */
  unit: string;
  /** 고정 가격. `null` 이면 화면이 카탈로그에서 채운다 */
  price: string | null;
  cta: string;
  /** 같은 페이지 안의 이동 대상 */
  anchor: string;
  /** "언제 이 플랜인가" 한 줄 */
  fit: string;
  benefits: string[];
  recommended?: boolean;
}

export const PLAN_CARDS: PlanCard[] = [
  {
    key: 'single',
    name: '단건 계약',
    who: '지금 필요한 한 건만 빠르게 처리하고 싶은 경우',
    unit: '부터 · 건당',
    price: null,
    cta: '단건 요금 보기',
    anchor: PLAN_ANCHOR.priceGuide,
    fit: '단건 사안이 생길 때',
    benefits: ['항목별 시작가를 사전 공개 — 예산 예측 가능', '계약 체결 후 24시간 내 담당 변호사 배정', '긴급 납기(24시간) +30%로 우선 처리'],
  },
  {
    key: 'membership',
    name: '월 멤버십',
    who: '계약·자문이 반복되는 성장 단계 기업',
    unit: `부터 · ${MEMBERSHIP_ROWS.map(row => row.name).join('/')}`,
    // 가장 가벼운 등급의 월 요금. 등급이 바뀌면 상수 파일만 고치면 된다.
    price: `월 ${MEMBERSHIP_ROWS[0].monthlyPrice}`,
    cta: '멤버십 보기',
    anchor: PLAN_ANCHOR.membership,
    fit: '월 2건 이상 검토가 반복될 때',
    benefits: ['월 한도 내 계약서 검토·자문 상시 이용', '담당 변호사가 맥락을 이어서 처리', '분쟁으로 번지면 송무팀 즉시 연계'],
    recommended: true,
  },
];

export const PRICING_SECTIONS: PricingSection[] = [
  // ── 01 단건 ────────────────────────────────────────────────────────────
  {
    kind: 'service-groups',
    id: PLAN_ANCHOR.priceGuide,
    label: '단건 계약',
    features: [
      { ttl: '투명한 단가 공개', dsc: '사전 가격 가이드 확인 후 진행' },
      { ttl: '즉시 개시', dsc: '계약 체결 후 24시간 이내 담당 변호사 배정' },
      { ttl: '긴급 납기 지원', dsc: '24시간 이내 처리는 +30% 가산으로 우선 진행' },
    ],
    // 상품·금액은 서버 카탈로그에서 온다 — 여기에 표를 다시 만들지 말 것.
    notes: ['긴급 납기(24시간 이내) +30% 가산', '작성(신규 초안)은 검토 대비 약 20~30% 높게 산정'],
  },

  // ── 02 번들 ────────────────────────────────────────────────────────────
  // NOTE: 번들 계약은 우선 노출 보류 (주석 처리). 복원 시 아래 블록을 다시 활성화하고
  //       nav-config.ts / Footer.tsx 의 번들 링크도 함께 복원할 것.
  // {
  //   kind: 'bundle-groups',
  //   id: 'bundle',
  //   label: '번들 계약',
  //   title: '번들 계약 안내',
  //   intro: '목적별 서비스 조합을 한 번에. 단건 합산 대비 최대 15% 절감, 유효기간 6개월.',
  //   features: [
  //     { ttl: '최대 15% 절감', dsc: '단건 합산 대비 번들가 자동 적용' },
  //     { ttl: '6개월 사용 기한', dsc: '계약일로부터 6개월 내 자유롭게 차감' },
  //     { ttl: '목적별 4가지 조합', dsc: '필승계약 · Seed Kit · Growth Kit · 해외진출' },
  //   ],
  //   bundleGroups: [
  //     {
  //       name: '필승계약 번들',
  //       tagline: '계약서 한 장이 분쟁을 막는 가장 강력한 수단입니다',
  //       tiers: [
  //         { name: 'K-베이직', inclusions: ['국문 계약서 작성 및 검토', '법률자문·상담 3회'], original: '1,050,000원', price: '900,000원' },
  //         { name: 'K-스탠다드', inclusions: ['국문 계약서 작성 및 검토', '법률자문·상담 4회'], original: '1,400,000원', price: '1,200,000원', featured: true, tag: '추천' },
  //         { name: 'K-프리미엄', inclusions: ['국문 계약서 작성 및 검토', '법률자문·상담 5회'], original: '1,750,000원', price: '1,500,000원' },
  //       ],
  //       targetList: ['거래처와의 계약서를 처음 작성해야 하는 스타트업·중소기업', '상대방이 제시한 계약서 내 불리한 조항이 걱정되는 경우', '계약 조건 협상 전 법적 근거와 전략이 필요한 경우'],
  //     },
  //     {
  //       emoji: '🌱',
  //       name: 'Seed Kit — 법인 설립 자체에 집중',
  //       tagline: '정관 작성(템플릿 기반)을 저렴하게, 동업·주주간 계약은 단계적으로',
  //       tiers: [
  //         { name: 'Basic', inclusions: ['정관 작성 (템플릿 기반)', '법률자문 1회'], original: '900,000원~', price: '630,000원~' },
  //         { name: 'Standard', inclusions: ['정관 작성', '동업계약 검토', '법률자문 2회'], original: '1,450,000원~', price: '1,015,000원~', featured: true },
  //         { name: 'Premium', inclusions: ['정관 작성', '동업계약 작성', '주주간계약 작성', '법률자문 3회'], original: '2,200,000원~', price: '1,540,000원~' },
  //       ],
  //     },
  //     {
  //       emoji: '🚀',
  //       name: 'Growth Kit — 투자 클로징 패키지',
  //       tagline: '주주간계약 + 이사회/주총 의사록이 한 세트로 진행되는 투자 계약 흐름',
  //       tiers: [
  //         { name: 'Basic', inclusions: ['스톡옵션 계약 검토', '법률자문 의견서'], original: '900,000원~', price: '765,000원~' },
  //         {
  //           name: 'Standard',
  //           inclusions: ['투자계약 검토', '주주간계약 작성', '이사회 or 주총 의사록', '법률자문 2회'],
  //           original: '2,500,000원~',
  //           price: '2,125,000원~',
  //           featured: true,
  //         },
  //         {
  //           name: 'Premium',
  //           inclusions: ['투자계약 작성', '스톡옵션 작성', '주주간계약 작성', '유상증자 법률검토 의견서', '이사회 or 주총 의사록', '법률자문 3회'],
  //           original: '3,900,000원~',
  //           price: '3,315,000원~',
  //         },
  //       ],
  //     },
  //     {
  //       name: '해외진출 번들',
  //       tagline: '글로벌 거래 관행과 법률 리스크를 동시에 잡습니다',
  //       tiers: [
  //         { name: 'Basic (NDA·기밀보호 등)', inclusions: ['글로벌 계약서 작성 및 검토', '법률자문 의견서'], original: '1,350,000원', price: '1,140,000원' },
  //         {
  //           name: 'Standard (공급·거래계약 등)',
  //           inclusions: ['글로벌 계약서 작성 및 검토', '법률자문 의견서', 'A/S 자문 1회'],
  //           original: '1,950,000원',
  //           price: '1,650,000원',
  //           featured: true,
  //         },
  //         {
  //           name: 'Premium (투자·파트너십 등)',
  //           inclusions: ['글로벌 계약서 작성 및 검토', '법률자문 의견서', 'A/S 자문 2회'],
  //           original: '2,500,000원',
  //           price: '2,100,000원',
  //         },
  //       ],
  //       targetList: [
  //         '해외 업체와 NDA·공급계약·투자계약 체결을 앞두고 있는 기업',
  //         '단순 번역이 아닌 법률적 의미까지 정확히 반영하고 싶은 경우',
  //         '글로벌 거래 관행과 국내 법률 리스크를 동시에 검토 받고 싶은 경우',
  //       ],
  //     },
  //   ],
  // },

  // ── 03 단체 ────────────────────────────────────────────────────────────
  {
    kind: 'group-table',
    id: PLAN_ANCHOR.group,
    label: '단체 계약',
    features: [
      { ttl: '회원 우대 단가', dsc: '소속 회원사 대상 단건 서비스 10~15% 우선 할인' },
      { ttl: '단체 전담 변호사', dsc: '대표 담당자 + 회원사 우선 상담 채널 개설' },
      { ttl: '맞춤형 세미나', dsc: '회원사 대상 뉴스레터 및 법률 세미나 제공' },
    ],
  },

  // ── 04 멤버십 ──────────────────────────────────────────────────────────
  {
    kind: 'memberships',
    id: PLAN_ANCHOR.membership,
    label: '월 멤버십',
    features: [
      { ttl: '금액 크레딧 정액제', dsc: '매월 1일 갱신 · 예측 가능한 비용' },
      { ttl: '이월 가능', dsc: '사전 협의 시 미사용 크레딧 차월 이월' },
      { ttl: '납부액보다 큰 크레딧', dsc: '상위 등급일수록 혜택이 커집니다' },
    ],
    rows: MEMBERSHIP_ROWS,
    notes: [
      '계약서·법률문서·의사록·법률자문 등 LawKit 모든 단건 서비스에 금액 단위로 자유롭게 차감',
      creditNote,
      '담당 변호사가 수행한 작업 금액 기준 차감 · 사전 협의 시 미사용 크레딧 차월 이월',
      '크레딧을 초과한 사용분은 별도 청구(VAT 별도)',
      '초과 사용 2개월 연속 시 상위 플랜 자동 제안',
    ],
  },
];

/**
 * 섹션 id → `/plan` 안의 위치. 옛 `TabNav.tabHref` 를 대신한다.
 *
 * 헤더 메뉴·푸터·모바일 서랍이 전부 이 함수를 쓴다. 세 곳이 각자 `'/plan#' + id` 를
 * 조립하면 앵커 하나를 고칠 때 한 곳이 남는다.
 */
export const planSectionHref = (id: string): string => `/plan#${id}`;

// ── 구획이 그리는 문구 ──────────────────────────────────────────────────
//
// 아래 셋은 `PRICING_SECTIONS` 에서 뽑아 쓴다. 화면에 직접 적지 않는 이유는 모바일
// 서랍·푸터가 같은 목록을 보고 있어서다 — 한쪽만 고치면 메뉴와 본문이 다른 말을 한다.

const findSection = <K extends PricingSection['kind']>(kind: K): Extract<PricingSection, { kind: K }> => {
  const section = PRICING_SECTIONS.find((item): item is Extract<PricingSection, { kind: K }> => item.kind === kind);
  // 섹션은 같은 파일의 리터럴이라 못 찾는 경우는 오타뿐이다. 조용히 빈 화면을 그리느니 멈춘다.
  if (!section) throw new Error(`요금 구획이 없다: ${kind}`);
  return section;
};

/** 요금표 아래 한 줄짜리 단서들. */
export const SINGLE_NOTES: string[] = findSection('service-groups').notes ?? [];

/** 멤버십 카드 아래 ※ 목록. 초과 청구·이월처럼 **안 적으면 분쟁이 되는** 조건들이다. */
export const MEMBERSHIP_NOTES: string[] = findSection('memberships').notes ?? [];

/** 단체 카드의 체크 목록. */
export const GROUP_PERKS: string[] = findSection('group-table').features.map(feature => feature.dsc);
